import { PeriodName, ProductType } from '../graphql';
import { Path } from './enums';

export const paths: Record<Exclude<Path, Path.COMPANY | Path.PERIOD>, string> = {
  [Path.MAINTENANCE_MODE]: 'maintenance',
  [Path.LOGIN]: 'login',
  [Path.SIGNUP]: 'signup',
  [Path.RESET_PASSWORD]: 'reset-password',
  [Path.VERIFY_EMAIL]: 'verify-email',
  [Path.VERIFY_EMAIL_SENT]: 'verify-email-sent',
  [Path.ACCEPT_INVITATION]: 'accept-invitation',
  [Path.ENTERPRISE_SSO]: 'enterprise-sso',
  [Path.PROFILE]: 'profile',
  [Path.LOGOUT]: 'logout',
  [Path.COMPANIES]: 'companies',

  // R&D
  [Path.RD_TAX_CREDIT]: 'rd',
  [Path.COMPANY_INFO]: 'company-info',
  [Path.RD_PROJECTS]: 'rd-projects',
  [Path.PAYROLL_QRE]: 'rd-payroll',
  [Path.ACCOUNTING_QRE]: 'rd-accounting',
  [Path.CREDIT_CALCULATION]: 'rd-credit-calculation',
  [Path.RD_DATA_SOURCES]: 'rd-data-sources',
  [Path.EXPORT_TAX]: 'export-tax',

  // SR&ED
  [Path.SRED_TAX_CREDIT]: 'sred',
  [Path.SRED_PROJECTS]: 'sred-projects',
  [Path.SRED_PAYROLL_EXPENSES]: 'sred-payroll-expenses',

  // ASC
  [Path.ASC_350_40]: 'asc',
  [Path.ASC_PROJECTS]: 'asc-projects',
  [Path.ASC_WAGES]: 'asc-wages',
  [Path.ASC_DATA_SOURCES]: 'asc-data-sources',
  [Path.EXPORT_ASC]: 'export-asc',

  [Path.TEST_LARGE_FILE_UPLOAD]: 'test-large-file-upload',
};

export type GetPathInput =
  | {
      path:
        | Path.MAINTENANCE_MODE
        | Path.LOGIN
        | Path.SIGNUP
        | Path.RESET_PASSWORD
        | Path.VERIFY_EMAIL
        | Path.VERIFY_EMAIL_SENT
        | Path.ACCEPT_INVITATION
        | Path.ENTERPRISE_SSO
        | Path.PROFILE
        | Path.LOGOUT
        | Path.COMPANIES;
      companyId?: never;
      fiscalYearId?: never;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.COMPANY;
      companyId: string;
      fiscalYearId?: never;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.TEST_LARGE_FILE_UPLOAD;
      companyId: string;
      fiscalYearId?: never;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.RD_TAX_CREDIT;
      companyId: string;
      fiscalYearId?: string;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.SRED_TAX_CREDIT;
      companyId: string;
      fiscalYearId?: string;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path:
        | Path.COMPANY_INFO
        | Path.RD_DATA_SOURCES
        | Path.PAYROLL_QRE
        | Path.ACCOUNTING_QRE
        | Path.CREDIT_CALCULATION
        | Path.EXPORT_TAX;
      companyId: string;
      fiscalYearId: string;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.RD_PROJECTS;
      companyId: string;
      fiscalYearId: string;
      periodName?: never;
      rdProjectId?: string;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.SRED_PROJECTS;
      companyId: string;
      fiscalYearId: string;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: string;
    }
  | {
      path: Path.SRED_PAYROLL_EXPENSES;
      companyId: string;
      fiscalYearId: string;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.ASC_350_40;
      companyId: string;
      fiscalYearId?: string;
      periodName?: string;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    }
  | {
      path: Path.ASC_DATA_SOURCES | Path.ASC_PROJECTS | Path.EXPORT_ASC | Path.ASC_WAGES;
      companyId: string;
      fiscalYearId: string;
      periodName: PeriodName;
      rdProjectId?: never;
      ascProjectId?: string;
      sredProjectId?: never;
    }
  | {
      path: Path.SRED_TAX_CREDIT;
      companyId: string;
      fiscalYearId?: string;
      periodName?: never;
      rdProjectId?: never;
      ascProjectId?: never;
      sredProjectId?: never;
    };

export const getPath = ({
  ascProjectId,
  companyId,
  fiscalYearId,
  path,
  periodName,
  rdProjectId,
  sredProjectId,
}: GetPathInput): string => {
  switch (path) {
    case Path.MAINTENANCE_MODE:
    case Path.LOGIN:
    case Path.SIGNUP:
    case Path.RESET_PASSWORD:
    case Path.VERIFY_EMAIL:
    case Path.VERIFY_EMAIL_SENT:
    case Path.ACCEPT_INVITATION:
    case Path.ENTERPRISE_SSO:
    case Path.PROFILE:
    case Path.LOGOUT:
    case Path.COMPANIES:
      return `/${paths[path]}`;
    case Path.COMPANY:
      return `/${paths[Path.COMPANIES]}/${companyId}`;
    case Path.TEST_LARGE_FILE_UPLOAD:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[path]}`;

    // R&D Routes
    case Path.RD_TAX_CREDIT:
      // Setting default R&D path to projects
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.RD_TAX_CREDIT]}`;
    case Path.COMPANY_INFO:
    case Path.RD_DATA_SOURCES:
    case Path.PAYROLL_QRE:
    case Path.ACCOUNTING_QRE:
    case Path.CREDIT_CALCULATION:
    case Path.EXPORT_TAX:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.RD_TAX_CREDIT]}/${fiscalYearId}/${PeriodName.A}/${
        paths[path]
      }`;
    case Path.RD_PROJECTS:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.RD_TAX_CREDIT]}/${fiscalYearId}/${PeriodName.A}/${
        paths[path]
      }${rdProjectId ? `/${rdProjectId}` : ''}`;

    // SR&ED Routes (Canadian Tax Credit)
    case Path.SRED_TAX_CREDIT:
      // Setting default SR&ED path to projects
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.SRED_TAX_CREDIT]}`;
    case Path.SRED_PROJECTS:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.SRED_TAX_CREDIT]}/${fiscalYearId}/${PeriodName.A}/${
        paths[path]
      }${sredProjectId ? `/${sredProjectId}` : ''}`;
    case Path.SRED_PAYROLL_EXPENSES:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.SRED_TAX_CREDIT]}/${fiscalYearId}/${PeriodName.A}/${
        paths[path]
      }`;

    // ASC 350-40 Routes
    case Path.ASC_350_40:
      // Setting default ASC 350-40 path to projects
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.ASC_350_40]}`;
    case Path.ASC_WAGES:
    case Path.ASC_DATA_SOURCES:
    case Path.EXPORT_ASC:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.ASC_350_40]}/${fiscalYearId}/${periodName}/${
        paths[path]
      }`;
    case Path.ASC_PROJECTS:
      return `/${paths[Path.COMPANIES]}/${companyId}/${paths[Path.ASC_350_40]}/${fiscalYearId}/${periodName}/${
        paths[path]
      }${ascProjectId ? `/${ascProjectId}` : ''}`;
  }
};

export const getPathUrl = (input: GetPathInput): string => {
  const url = new URL(getPath(input), window.location.origin);
  return url.toString();
};

export type GetDefaultAscPathOptions = { companyId: string; fiscalYearId: string; periodName: PeriodName };

export const getDefaultAscPath = ({ companyId, fiscalYearId, periodName }: GetDefaultAscPathOptions): string =>
  getPath({ path: Path.ASC_PROJECTS, companyId, fiscalYearId, periodName });

export type GetDefaultRdPathOptions = { companyId: string; fiscalYearId: string };

export const getDefaultRdPath = ({ companyId, fiscalYearId }: GetDefaultRdPathOptions): string =>
  getPath({ path: Path.COMPANY_INFO, companyId, fiscalYearId });

export type GetDefaultSredPathOptions = { companyId: string; fiscalYearId: string };

export const getDefaultSredPath = ({ companyId, fiscalYearId }: GetDefaultSredPathOptions): string =>
  getPath({ path: Path.SRED_PROJECTS, companyId, fiscalYearId });

export type GetProductDefaultPathOptions = {
  product: ProductType;
  companyId: string;
};

export const getProductDefaultPath = ({ companyId, product }: GetProductDefaultPathOptions): string => {
  let targetPath: Path.RD_TAX_CREDIT | Path.SRED_TAX_CREDIT | Path.ASC_350_40;

  switch (product) {
    case ProductType.UsRd:
      targetPath = Path.RD_TAX_CREDIT;
      break;
    case ProductType.CaSred:
      targetPath = Path.SRED_TAX_CREDIT;
      break;
    case ProductType.UsAsc:
      targetPath = Path.ASC_350_40;
      break;
  }

  return getPath({ path: targetPath, companyId });
};
